import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart2, TrendingUp, TrendingDown, Target,
  ArrowLeft, Activity, Tag, Flame, Share2, X, Loader,
  BookOpen, ChevronRight, ChevronDown,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import { formatPrice } from '../utils/formatters';
import TradeCard from '../components/TradeCard';
import ShareToForumModal from '../components/ShareToForumModal';
import PerformanceShareCard from '../components/PerformanceShareCard';
import { captureAndShare } from '../utils/shareImage';
import { StatGridSkeleton } from '../components/SkeletonLoader';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const TIMEFRAME_DAYS = { '1M': 30, '3M': 90, '6M': 180, '1Y': 365 };

function fmtHold(hours) {
  if (!hours) return '—';
  if (hours >= 48) return `${(hours / 24).toFixed(1)} days`;
  return `${hours}h`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
}

function rrContext(rr) {
  if (!rr) return { label: '—', color: null };
  if (rr >= 2) return { label: 'Great', color: '#16C784' };
  if (rr >= 1) return { label: 'Acceptable', color: '#F59E0B' };
  return { label: 'Below 1:1', color: '#EF4444' };
}


function SectionLabel({ label }) {
  const { colors } = useTheme();
  return (
    <p className="text-[11px] font-semibold uppercase tracking-wider px-0.5" style={{ color: colors.secondaryText }}>
      {label}
    </p>
  );
}

const ANALYTICS_NUDGES = [
  { level: 12, color: '#6366f1', label: 'Validate Before You Trade', sub: 'How to read your own performance data' },
  { level: 10, color: '#f59e0b', label: 'Controlled Practice',       sub: 'Build consistency through structured review' },
];

function LearningNudge({ navigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: open ? 'rgba(255,255,255,0.03)' : 'linear-gradient(135deg, #6366f118 0%, #6366f108 100%)', border: open ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(99,102,241,0.35)', transition: 'background 0.2s, border-color 0.2s' }}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" style={{ color: open ? 'rgba(255,255,255,0.4)' : '#6366f1' }} />
          <p className="text-[11px] font-black uppercase tracking-widest" style={{ color: open ? 'rgba(255,255,255,0.4)' : '#a5b4fc' }}>Understand your numbers</p>
        </div>
        <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200" style={{ color: open ? 'rgba(255,255,255,0.25)' : '#6366f199', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-1.5">
          {ANALYTICS_NUDGES.map(({ level, color, label, sub }) => (
            <button key={level} onClick={() => navigate(`/learning?view=level&level=${level}&card=0`)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-left"
              style={{ background: `${color}0d`, border: `1px solid ${color}22` }}>
              <div className="flex items-center gap-2.5">
                <BookOpen className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                <div>
                  <p className="text-[11px] font-semibold leading-snug" style={{ color }}>{label}</p>
                  <p className="text-[9px] leading-snug" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub}</p>
                </div>
              </div>
              <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: `${color}50` }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TradingAnalyticsPage() {
  const { colors } = useTheme();
  const { isAdmin, canAccess } = usePermissions();
  const canSocialShare = isAdmin || canAccess('social:share');
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [resultsData, setResultsData] = useState(null);
  const [disciplineData, setDisciplineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('3M');
  const [shareOpen, setShareOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const shareCardRef = useRef(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, resultsRes, disciplineRes] = await Promise.allSettled([
        axios.get(`${API_URL}/trade/history`),
        axios.get(`${API_URL}/results/summary`),
        axios.get(`${API_URL}/discipline/score`),
      ]);
      if (tradesRes.status === 'fulfilled') setTrades(tradesRes.value.data || []);
      if (resultsRes.status === 'fulfilled') setResultsData(resultsRes.value.data);
      if (disciplineRes.status === 'fulfilled') setDisciplineData(disciplineRes.value.data || null);
    } catch (e) {
      console.error('Failed to fetch analytics data', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrades = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - TIMEFRAME_DAYS[timeframe]);
    return trades.filter(t => t.status === 'CLOSED' && t.executed_at && new Date(t.executed_at) >= cutoff);
  }, [trades, timeframe]);

  const winningTrades = filteredTrades.filter(t => t.pnl > 0);
  const losingTrades  = filteredTrades.filter(t => t.pnl < 0);
  const totalTrades   = filteredTrades.length;
  const winRate       = totalTrades > 0 ? ((winningTrades.length / totalTrades) * 100).toFixed(1) : 0;
  const totalPnL      = filteredTrades.reduce((sum, t) => sum + (t.pnl || 0), 0);
  const avgWin        = winningTrades.length > 0 ? winningTrades.reduce((s, t) => s + t.pnl, 0) / winningTrades.length : 0;
  const avgLoss       = losingTrades.length  > 0 ? losingTrades.reduce((s, t) => s + t.pnl, 0) / losingTrades.length  : 0;
  const profitFactor  = avgLoss !== 0 ? Math.abs(avgWin / avgLoss) : 0;

  const maxDrawdown = useMemo(() => {
    let peak = 0; let cum = 0; let maxDD = 0;
    for (const t of [...filteredTrades].sort((a, b) => new Date(a.executed_at) - new Date(b.executed_at))) {
      cum += t.pnl || 0;
      if (cum > peak) peak = cum;
      const dd = peak - cum;
      if (dd > maxDD) maxDD = dd;
    }
    return filteredTrades.length < 2 ? null : maxDD;
  }, [filteredTrades]);

  const strategyBreakdown = useMemo(() => {
    const map = {};
    filteredTrades.forEach(t => {
      const tags = t.strategy_tags?.length > 0 ? t.strategy_tags : ['Untagged'];
      tags.forEach(tag => {
        if (!map[tag]) map[tag] = { tag, trades: 0, wins: 0, pnl: 0 };
        map[tag].trades++;
        if (t.pnl > 0) map[tag].wins++;
        map[tag].pnl += t.pnl || 0;
      });
    });
    return Object.values(map).sort((a, b) => b.trades - a.trades);
  }, [filteredTrades]);

  const s    = resultsData?.summary    || {};
  const a    = resultsData?.analytics  || {};
  const perf = resultsData?.performers || { top: [], bottom: [] };
  const recentTrades = resultsData?.recent_trades || [];
  const hasData = (s.closed_trades || 0) > 0 || totalTrades > 0;

  const disciplineScore     = disciplineData?.discipline_score ?? 0;
  const streak = a.current_streak || 0;
  const streakColor = streak > 0 ? colors.profit : streak < 0 ? colors.loss : colors.secondaryText;
  const streakSentence = streak > 0
    ? `On a ${streak}-win streak`
    : streak < 0
    ? `On a ${Math.abs(streak)}-loss streak`
    : 'No active streak';

  const winRatePct   = s.win_rate || 0;
  const winRateColor = winRatePct >= 55 ? '#16C784' : winRatePct >= 40 ? '#F59E0B' : '#EF4444';
  const winRateLabel = winRatePct >= 55 ? 'Strong' : winRatePct >= 40 ? 'Average' : 'Below target';
  const rr = rrContext(a.avg_rr);

  if (loading) return <StatGridSkeleton count={6} />;

  return (
    <div className="space-y-4 pb-4" data-testid="trading-analytics-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="app-card p-2 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
            <ArrowLeft className="w-4 h-4" style={{ color: colors.primaryText }} />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.primaryText }}>Trading Analytics</h1>
            <p className="text-xs" style={{ color: colors.secondaryText }}>Your performance overview</p>
          </div>
        </div>
        {hasData && (
          <div className="flex items-center gap-2">
            {canSocialShare && (
              <button
                onClick={() => setSocialOpen(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold"
                style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}>
                <Share2 className="w-3 h-3" /> Post
              </button>
            )}
            <button
              onClick={() => setShareOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold"
              style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
              <Share2 className="w-3 h-3" /> Forum
            </button>
          </div>
        )}
      </div>

      {/* Learning nudges */}
      <LearningNudge navigate={navigate} />

      {/* Timeframe Selector */}
      <div data-tour="analytics-timeframe" className="app-card flex space-x-1 p-1 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
        {['1M', '3M', '6M', '1Y'].map(tf => (
          <button key={tf} onClick={() => setTimeframe(tf)}
            className="flex-1 py-1.5 text-xs rounded font-medium transition-colors"
            style={{ backgroundColor: timeframe === tf ? colors.accent : 'transparent', color: timeframe === tf ? '#fff' : colors.secondaryText }}>
            {tf}
          </button>
        ))}
      </div>

      {!hasData ? (
        <div className="app-card rounded-lg p-8 text-center"
          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
          <BarChart2 className="w-10 h-10 mx-auto mb-3" style={{ color: colors.secondaryText }} />
          <p className="text-[14px] font-medium" style={{ color: colors.primaryText }}>No trades yet</p>
          <p className="text-[13px] mt-1" style={{ color: colors.secondaryText }}>Place trades to see your analysis here</p>
        </div>
      ) : (
        <>
          {/* Open positions banner */}
          {s.open_positions > 0 && (
            <div className="rounded-lg px-3 py-1.5 flex items-center justify-between"
              style={{ backgroundColor: `${colors.accent}15`, borderWidth: '1px', borderColor: colors.accent }}>
              <span className="text-[13px]" style={{ color: colors.accent }}>
                {s.open_positions} open position{s.open_positions > 1 ? 's' : ''} — P&L updates on close
              </span>
              <span className="text-[13px] font-semibold" style={{ color: colors.secondaryText }}>{s.closed_trades} closed</span>
            </div>
          )}

          {/* -- Overview -- */}
          <SectionLabel label={`Overview — ${timeframe}`} />
          <div data-tour="analytics-summary" className="app-card rounded-lg overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
            {/* Top row: P&L + W/L counts */}
            <div className="flex items-stretch" style={{ borderBottom: `1px solid ${colors.divider}` }}>
              <div className="flex-1 px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>P&L ({timeframe})</p>
                <p className="text-[24px] font-bold leading-tight"
                  style={{ color: totalPnL >= 0 ? colors.profit : colors.loss }}>
                  {totalPnL >= 0 ? '+' : ''}{formatPrice(totalPnL)}
                </p>
              </div>
              <div style={{ width: 1, backgroundColor: colors.divider }} />
              <div className="px-3 py-2.5 flex flex-col justify-center items-center" style={{ minWidth: 90 }}>
                <p className="text-[11px] uppercase tracking-wide mb-1" style={{ color: colors.secondaryText }}>Trades</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[13px] font-bold px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: `${colors.profit}18`, color: colors.profit }}>{winningTrades.length}W</span>
                  <span className="text-[13px] font-bold px-2 py-0.5 rounded-md"
                    style={{ backgroundColor: `${colors.loss}18`, color: colors.loss }}>{losingTrades.length}L</span>
                </div>
              </div>
            </div>
            {/* Bottom row: win rate bar + profit factor */}
            <div className="px-3 py-2.5">
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-[12px]" style={{ color: colors.secondaryText }}>Win Rate</span>
                <div className="flex items-center gap-2">
                  <span className="text-[12px]" style={{ color: winRateColor }}>{winRate}%</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${winRateColor}18`, color: winRateColor }}>{winRateLabel}</span>
                  <span className="text-[11px]" style={{ color: colors.secondaryText }}>PF {profitFactor.toFixed(2)}</span>
                </div>
              </div>
              <div className="w-full rounded-full overflow-hidden" style={{ height: 5, backgroundColor: colors.progressTrack }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${winRate}%`, backgroundColor: winRateColor }} />
              </div>
            </div>
          </div>

          {/* -- Performance -- */}
          <SectionLabel label="Performance" />
          <div data-tour="analytics-performance" className="app-card rounded-lg overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
            <div className="flex items-stretch" style={{ borderBottom: `1px solid ${colors.divider}` }}>
              <div className="flex-1 px-3 py-2.5">
                <div className="flex items-center gap-1 mb-1">
                  <TrendingUp className="w-2.5 h-2.5" style={{ color: colors.profit }} />
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Avg Win</p>
                </div>
                <p className="text-[15px] font-bold" style={{ color: colors.profit }}>{formatPrice(s.avg_win)}</p>
              </div>
              <div style={{ width: 1, backgroundColor: colors.divider }} />
              <div className="flex-1 px-3 py-2.5">
                <div className="flex items-center gap-1 mb-1">
                  <TrendingDown className="w-2.5 h-2.5" style={{ color: colors.loss }} />
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Avg Loss</p>
                </div>
                <p className="text-[15px] font-bold" style={{ color: colors.loss }}>{formatPrice(s.avg_loss)}</p>
              </div>
              <div style={{ width: 1, backgroundColor: colors.divider }} />
              <div className="flex-1 px-3 py-2.5">
                <div className="flex items-center gap-1 mb-1">
                  <Target className="w-2.5 h-2.5" style={{ color: colors.accent }} />
                  <p className="text-[11px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Avg R:R</p>
                </div>
                <p className="text-[15px] font-bold" style={{ color: rr.color || colors.primaryText }}>
                  {a.avg_rr ? `${a.avg_rr}x` : '…'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: maxDrawdown > 0 ? `${colors.loss}08` : `${colors.profit}08` }}>
              <p className="text-[11px]" style={{ color: colors.secondaryText }}>Max drawdown</p>
              <p className="text-[12px] font-semibold" style={{ color: maxDrawdown > 0 ? colors.loss : colors.secondaryText }}>
                {maxDrawdown > 0 ? `-${formatPrice(maxDrawdown)}` : '…'}
              </p>
            </div>
            {rr.label !== '—' && (
              <div className="px-3 py-2" style={{ backgroundColor: `${rr.color}0a`, borderTop: `1px solid ${colors.divider}` }}>
                <p className="text-[11px]" style={{ color: rr.color }}>
                  R:R {rr.label} — {a.avg_rr >= 2 ? 'risking less to make more' : a.avg_rr >= 1 ? 'targets cover risk' : "targets don't justify risk taken"}
                </p>
              </div>
            )}
          </div>


          {/* -- Streaks -- */}
          <SectionLabel label="Streaks" />
          <div className="app-card rounded-lg p-3"
            style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>
            <div className="flex items-center gap-1.5 mb-2">
              <Flame className="w-3.5 h-3.5" style={{ color: streakColor }} />
              <p className="text-[13px] font-semibold" style={{ color: streakColor }}>{streakSentence}</p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: 'Current', value: streak === 0 ? '…' : streak > 0 ? `${streak}-win` : `${Math.abs(streak)}-loss`, color: streakColor },
                { label: 'Best run', value: a.max_win_streak ? `${a.max_win_streak}W` : '…', color: colors.profit },
                { label: 'Worst run', value: a.max_loss_streak ? `${a.max_loss_streak}L` : '…', color: colors.loss },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg py-1.5" style={{ backgroundColor: colors.inputBg }}>
                  <p className="text-[11px] mb-0.5" style={{ color: colors.secondaryText }}>{label}</p>
                  <p className="text-[14px] font-bold" style={{ color }}>{value}</p>
                </div>
              ))}
            </div>
          </div>




          {/* -- Breakdown -- */}
          {(strategyBreakdown.length > 0 || s.best_trade || s.worst_trade || perf.top.length > 0 || perf.bottom.length > 0) && (
            <>
              <SectionLabel label="Breakdown" />
              <div className="app-card rounded-lg overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderWidth: '1px', borderColor: colors.border }}>

                {/* Best trade / Worst trade */}
                {(s.best_trade || s.worst_trade) && (
                  <div className="grid grid-cols-2" style={{ borderBottom: `1px solid ${colors.divider}` }}>
                    {s.best_trade ? (
                      <button onClick={() => navigate(`/trade/${s.best_trade.trade_id || s.best_trade.id}`)}
                        className="flex flex-col gap-0.5 px-3 py-2.5 text-left"
                        style={{ borderRight: `1px solid ${colors.divider}` }}>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Best Trade</p>
                        <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{s.best_trade.symbol?.replace('.NS', '')}</p>
                        <p className="text-[13px] font-bold" style={{ color: colors.profit }}>+{formatPrice(s.best_trade.pnl)}</p>
                      </button>
                    ) : <div />}
                    {s.worst_trade ? (
                      <button onClick={() => navigate(`/trade/${s.worst_trade.trade_id || s.worst_trade.id}`)}
                        className="flex flex-col gap-0.5 px-3 py-2.5 text-left">
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Worst Trade</p>
                        <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{s.worst_trade.symbol?.replace('.NS', '')}</p>
                        <p className="text-[13px] font-bold" style={{ color: colors.loss }}>{formatPrice(s.worst_trade.pnl)}</p>
                      </button>
                    ) : <div />}
                  </div>
                )}

                {/* Top stock / Worst stock */}
                {(perf.top.length > 0 || perf.bottom.length > 0) && (
                  <div className="grid grid-cols-2" style={{ borderBottom: strategyBreakdown.length > 0 ? `1px solid ${colors.divider}` : undefined }}>
                    {perf.top[0] ? (
                      <button onClick={() => navigate(`/trade-history?symbol=${encodeURIComponent(perf.top[0].symbol)}`)}
                        className="flex flex-col gap-0.5 px-3 py-2.5 text-left"
                        style={{ borderRight: `1px solid ${colors.divider}` }}>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Top Stock</p>
                        <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{perf.top[0].symbol.replace('.NS', '')}</p>
                        <p className="text-[11px]" style={{ color: colors.profit }}>+{formatPrice(perf.top[0].pnl)} — {perf.top[0].trades}t</p>
                      </button>
                    ) : <div />}
                    {perf.bottom[0] ? (
                      <button onClick={() => navigate(`/trade-history?symbol=${encodeURIComponent(perf.bottom[0].symbol)}`)}
                        className="flex flex-col gap-0.5 px-3 py-2.5 text-left">
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.secondaryText }}>Worst Stock</p>
                        <p className="text-[13px] font-semibold truncate" style={{ color: colors.primaryText }}>{perf.bottom[0].symbol.replace('.NS', '')}</p>
                        <p className="text-[11px]" style={{ color: colors.loss }}>{formatPrice(perf.bottom[0].pnl)} — {perf.bottom[0].trades}t</p>
                      </button>
                    ) : <div />}
                  </div>
                )}

                {/* Strategy tags */}
                {strategyBreakdown.length > 0 && (
                  <div className="px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wide mb-2" style={{ color: colors.secondaryText }}>By Tag</p>
                    {strategyBreakdown.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-1.5 border-b last:border-0"
                        style={{ borderColor: colors.divider }}>
                        <div className="flex items-center gap-2">
                          <Tag className="w-3 h-3 flex-shrink-0" style={{ color: colors.accent }} />
                          <div>
                            <p className="text-[12px] font-medium capitalize" style={{ color: colors.primaryText }}>{item.tag}</p>
                            <p className="text-[10px]" style={{ color: colors.secondaryText }}>{item.trades}t — {item.trades > 0 ? ((item.wins / item.trades) * 100).toFixed(0) : 0}% win</p>
                          </div>
                        </div>
                        <p className="text-[12px] font-semibold" style={{ color: item.pnl >= 0 ? colors.profit : colors.loss }}>
                          {item.pnl >= 0 ? '+' : ''}{formatPrice(item.pnl)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            </>
          )}

          {/* -- Recent Trades -- */}
          {recentTrades.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <SectionLabel label="Recent Trades" />
                <button onClick={() => navigate('/trade-history')}
                  className="text-[11px] font-semibold flex items-center gap-0.5"
                  style={{ color: colors.accent }}>
                  View all <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <div className="space-y-2">
                {recentTrades.slice(0, 3).map((trade, idx) => (
                  <TradeCard key={trade.trade_id || trade.id || idx} trade={trade} onClick={() => navigate(`/trade/${trade.trade_id || trade.id}`)} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <ShareToForumModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        attachments={[{
          attach_type: 'pnl_summary',
          snapshot: {
            timeframe,
            total_trades: totalTrades,
            total_pnl: totalPnL,
            win_rate_pct: parseFloat(winRate),
            avg_win: avgWin,
            avg_loss: avgLoss,
            best_trade_pnl: filteredTrades.length > 0 ? Math.max(...filteredTrades.map(t => t.pnl || 0)) : null,
            worst_trade_pnl: filteredTrades.length > 0 ? Math.min(...filteredTrades.map(t => t.pnl || 0)) : null,
            discipline_score: disciplineData?.discipline_score ?? null,
          }
        }]}
        defaultTitle={`Trading Analytics (${timeframe}) — ${winRate}% win rate — ?${Math.abs(Math.round(totalPnL)).toLocaleString('en-IN')} ${totalPnL >= 0 ? 'profit' : 'loss'}`}
        defaultLifecycle="trade_idea"
      />

      {/* Hidden card – off-screen at full 1080×1350 for capture */}
      <div style={{ position: 'fixed', left: -9999, top: 0, width: 1080, height: 1350, opacity: 0, pointerEvents: 'none', zIndex: -1 }}>
        <PerformanceShareCard
          summary={s}
          analytics={a}
          timeframe={timeframe}
          disciplineScore={disciplineScore}
          disciplineComponents={disciplineData?.component_scores}
          strategyBreakdown={strategyBreakdown}
          performers={perf}
          cardRef={shareCardRef}
        />
      </div>

      {socialOpen && (() => {
        const PREVIEW_W = 260;
        const SCALE     = PREVIEW_W / 1080;
        const CARD_H    = 1350;
        const PREVIEW_H = Math.round(CARD_H * SCALE); // ~325px – full card height scaled
        return (
          <div className="fixed inset-0 z-50 flex items-end justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setSocialOpen(false)}>
            <div
              className="w-full max-w-sm rounded-t-2xl"
              style={{ background: colors.cardBg, border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', maxHeight: '92vh' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Fixed header */}
              <div style={{ padding: '18px 20px 10px', flexShrink: 0 }}>
                <div className="flex items-center justify-between">
                  <p className="text-[15px] font-bold" style={{ color: colors.primaryText }}>Share Performance Card</p>
                  <button onClick={() => setSocialOpen(false)}><X className="w-4 h-4" style={{ color: colors.secondaryText }} /></button>
                </div>
              </div>
              {/* Scrollable preview */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
                <div style={{ width: PREVIEW_W, height: PREVIEW_H, margin: '0 auto', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ width: 1080, height: CARD_H, transform: `scale(${SCALE})`, transformOrigin: 'top left' }}>
                    <PerformanceShareCard
                      summary={s}
                      analytics={a}
                      timeframe={timeframe}
                      disciplineScore={disciplineScore}
                      disciplineComponents={disciplineData?.component_scores}
                      strategyBreakdown={strategyBreakdown}
                      performers={perf}
                    />
                  </div>
                </div>
              </div>
              {/* Fixed footer */}
              <div style={{ padding: '12px 20px 20px', flexShrink: 0 }}>
                <p className="text-[12px] text-center mb-3" style={{ color: colors.secondaryText }}>Opens share sheet — pick Instagram, WhatsApp, or save to camera roll.</p>
                <button
                  onClick={async () => { if (!shareCardRef.current) return; setCapturing(true); try { await captureAndShare(shareCardRef.current, 'performance.png'); } finally { setCapturing(false); setSocialOpen(false); } }}
                  disabled={capturing}
                  className="w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2"
                  style={{ background: colors.accent, color: '#fff', opacity: capturing ? 0.6 : 1 }}>
                  {capturing ? <><Loader className="w-4 h-4 animate-spin" /> Preparing…</> : <><Share2 className="w-4 h-4" /> Share as Image</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default TradingAnalyticsPage;
